import {
  AdjustmentsHorizontalIcon,
  Bars3Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MoonIcon,
  SunIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Cog8ToothIcon } from "@heroicons/react/24/solid";
import * as Sentry from "@sentry/react";
import axios from "axios";
import cn from "classnames";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  CASE_DRAG_MIME,
  useCaseCategories,
} from "../../context/CaseCategoriesContext";
import { useFormDesigner } from "../../context/FormDesignerContext";
import { useTheme } from "../../context/ThemeContext";
import {
  Case,
  CaseScore,
  ChecklistTemplate,
  Suggestion,
} from "../../db/schema";
import Logo from "../common/Logo";
import badgeSrc from "../../police-narratives-badge.png";
import {
  createBlankCase,
  processAudioFile,
} from "../../services/audioProcessingService";
import Button from "../common/Button";
import CustomToast from "../common/CustomToast";
import NewCaseDropdown from "../common/NewCaseDropdown";
import AudioPlayback from "./AudioPlayback";
import CaseCategories from "./CaseCategories";
import ChecklistModal from "./ChecklistModal";
import MenuItem from "./MenuItem";
import UploadModal from "./UploadModal";
import { FileConfig, defaultRecordingName } from "./uploadFileConfig";
import { saveFilesToLibrary } from "../../services/libraryUploadService";
import { OPEN_REPORT_UPLOAD_EVENT } from "./uploadLauncher";
import ResizeHandle from "../common/ResizeHandle";
import { useResizablePanel } from "../../hooks/useResizablePanel";
import { policiesApi } from "../Policies/api";
import { useAcademyBadge } from "../../academy/useAcademyBadge";

// Hidden from the sidebar while the Academy is being tested; /academy still works.
const SHOW_ACADEMY_NAV = false;

type OnNewCase = (
  newCase: Case,
  score?: CaseScore | undefined,
  suggestions?: Suggestion[],
  transcription?: {
    task: string;
    language: string;
    duration: number;
    text: string;
    words: {
      word: string;
      start: number;
      end: number;
    }[];
  } | null
) => void;

interface MenuProps {
  onNewCase: OnNewCase;
  isMobileCase: boolean;
  setIsMobileCase: (value: boolean) => void;
  onChecklistComplete: (newCase: Case) => void;
  onCaseDiscarded: (caseId: string) => void;
}

// Track cases that are currently being polled to prevent duplicates
const activePollers = new Set<string>();

// Move this to a shared utility file later if needed
export const pollProcessingStatus = async (
  caseId: string,
  {
    onSuccess,
    onError,
    interval = 2000,
    timeout = 30 * 60 * 1000, // 30 minutes
    toastId,
    successMessage,
    cleanupToast,
  }: {
    onSuccess: (data: any) => void;
    onError?: (error: string) => void;
    interval?: number;
    timeout?: number;
    toastId?: string;
    successMessage?: string;
    cleanupToast?: (caseId: string) => void;
  }
) => {
  // Prevent duplicate polling for the same case
  if (activePollers.has(caseId)) {
    console.log(`Already polling case ${caseId}, skipping duplicate`);
    return () => {}; // Return no-op cleanup
  }
  activePollers.add(caseId);

  const endTime = Date.now() + timeout;

  const cleanup = () => {
    activePollers.delete(caseId);
  };

  const poll = setInterval(async () => {
    try {
      const response = await axios.get(
        `/api/cases/${caseId}/processing-status`
      );

      if (response.data.status === "active") {
        clearInterval(poll);
        cleanup();
        // Fetch complete case data
        const caseResponse = await axios.get(`/api/cases/${caseId}`);

        // Only show toast if successMessage is provided
        if (successMessage) {
          toast.success(successMessage, { duration: 5000, id: toastId });
        }
        // Clean up toast registry
        cleanupToast?.(caseId);
        onSuccess(caseResponse.data);
      } else if (response.data.status === "error") {
        clearInterval(poll);
        cleanup();
        const errorMsg = "Error processing audio: " + response.data.error;
        // Only show toast if successMessage is provided (same pattern as success toast)
        if (successMessage) {
          toast.error(errorMsg, { duration: 5000, id: toastId });
        }
        // Clean up toast registry
        cleanupToast?.(caseId);
        onError?.(errorMsg);
      } else if (response.data.status === "discarded") {
        // Case was discarded (e.g., upload cancelled) - stop polling silently
        clearInterval(poll);
        cleanup();
        // Dismiss any associated toast
        if (toastId) {
          toast.dismiss(toastId);
        }
        // Clean up toast registry
        cleanupToast?.(caseId);
        // Don't call onError - this is expected when upload is cancelled
        console.log(`Case ${caseId} was discarded, stopping polling`);
      }

      // Stop polling if timeout reached
      if (Date.now() > endTime) {
        clearInterval(poll);
        cleanup();
        const timeoutMsg = "Processing timed out";
        // Only show toast if successMessage is provided (same pattern as success toast)
        if (successMessage) {
          toast.error(timeoutMsg, { id: toastId });
        }
        // Clean up toast registry
        cleanupToast?.(caseId);
        onError?.(timeoutMsg);
      }
    } catch (error) {
      console.error("Error polling status:", error);
      Sentry.captureException(error, {
        tags: {
          component: "Menu",
          action: "pollProcessingStatus",
        },
        extra: { caseId },
      });
      clearInterval(poll);
      cleanup();
      // Clean up toast registry
      cleanupToast?.(caseId);
      onError?.("Error checking processing status");
    }
  }, interval);

  // Return cleanup function
  return () => {
    clearInterval(poll);
    cleanup();
  };
};

const Menu = memo(function ({
  onNewCase,
  isMobileCase,
  setIsMobileCase,
  onChecklistComplete,
  onCaseDiscarded,
}: MenuProps) {
  const { user, checkAuthStatus, addons, subscriptionStatus } = useAuth();
  // Paying customers only: any agency member, or an active personal
  // subscription. Trial users don't get the Feature Requests item.
  const canUseFeatureRequests = Boolean(user?.agency_id) || subscriptionStatus === "active";
  // Lessons that shipped after the officer joined and have not been opened.
  const academyBadge = useAcademyBadge();
  const { isDesignerOpen } = useFormDesigner();
  const { resolvedTheme, setTheme } = useTheme();
  const { activeFilter, setActiveFilter, discardCase, isDraggingCase } =
    useCaseCategories();
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDialogElement>(null);
  const uploadModalRef = useRef<HTMLDialogElement>(null);
  // Files handed over by the Files page ("Create report"); UploadModal applies
  // each new array as its starting selection.
  const [uploadPrefill, setUploadPrefill] = useState<FileConfig[] | undefined>();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  // Highlight the Trash item while a case is dragged over it.
  const [isTrashDragOver, setIsTrashDragOver] = useState(false);

  const allowCaseDrop = (e: React.DragEvent) => {
    if (e.dataTransfer.types.includes(CASE_DRAG_MIME)) {
      e.preventDefault();
      setIsTrashDragOver(true);
    }
  };

  const handleTrashDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsTrashDragOver(false);
    const caseId = e.dataTransfer.getData(CASE_DRAG_MIME);
    if (caseId) discardCase(caseId);
  };
  const navPanel = useResizablePanel({
    storageKey: "pnai-navWidth",
    defaultWidth: 256,
    minWidth: 250,
    maxWidth: 420,
    side: "right",
    maxViewportFraction: 0.35,
  });
  const [isMenuCollapsed, setIsMenuCollapsed] = useState(() => {
    const saved = localStorage.getItem("pnai-isMenuCollapsed");
    return saved ? JSON.parse(saved) : false;
  });
  const [checklistTemplates, setChecklistTemplates] = useState<
    ChecklistTemplate[]
  >([]);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [hasEvidenceIntegration, setHasEvidenceIntegration] = useState(false);
  // Policies shows only once the department has uploaded at least one document.
  const [hasPolicyDocuments, setHasPolicyDocuments] = useState(false);

  // Setup custom error toast handler for audio processing
  useEffect(() => {
    (window as any).showCustomAudioErrorToast = (
      errorInfo: any,
      onDownload: () => void,
      toastId?: string
    ) => {
      toast.custom(
        <CustomToast
          variant="error"
          title={errorInfo.errorMessage}
          description={errorInfo.description}
          actionLabel={errorInfo.actionLabel}
          showCloseButton
          onAction={onDownload}
          size="md"
          onClose={() => toastId && toast.dismiss(toastId)}
        />,
        { id: toastId, duration: Infinity }
      );
    };

    return () => {
      delete (window as any).showCustomAudioErrorToast;
    };
  }, []);

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    console.log(event);
  };

  const handleNewCase = async () => {
    if (location.pathname !== "/cases") navigate("/cases");
    try {
      const newCase = await createBlankCase(user);
      if (newCase) {
        onNewCase(newCase, undefined, [], null);
        console.log("New case created:", newCase);
      } else {
        console.error("Failed to create new case");
      }
    } catch (error) {
      console.error("Error creating new case:", error);
      Sentry.captureException(error, {
        tags: {
          component: "Menu",
          action: "handleNewCase",
        },
      });
      checkAuthStatus();
    } finally {
      const elem = document.activeElement;
      if (elem && "blur" in elem) {
        (elem as HTMLElement).blur();
      }
    }
  };

  useEffect(() => {
    const onOpen = (e: Event) => {
      const files = (e as CustomEvent<FileConfig[]>).detail;
      if (!files?.length) return;
      setUploadPrefill([...files]);
      checkAuthStatus();
      uploadModalRef.current?.showModal();
    };
    window.addEventListener(OPEN_REPORT_UPLOAD_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_REPORT_UPLOAD_EVENT, onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnRecordAudioClick = () => {
    if (location.pathname !== "/cases") navigate("/cases");
    checkAuthStatus();
    modalRef.current?.showModal();
  };

  const handleOnUploadAudioClick = () => {
    if (location.pathname !== "/cases") navigate("/cases");
    checkAuthStatus();
    uploadModalRef.current?.showModal();
  };

  const handleAudioFileChange = async (
    filesOrEvent:
      | File
      | Blob
      | React.ChangeEvent<HTMLInputElement>
      | Array<{
          file: File;
          audioType: "narrative" | "interaction";
          speakers: { name: string; role: string }[];
          languages: string[];
          additionalContext: string;
          keepVideo?: boolean;
          axonSource?: {
            evidenceId: string;
            title: string;
            size: number;
            duration: number;
            contentType: string;
          };
        }>,
    optionsOrAudioType?:
      | string
      | {
          includeTimestamps: boolean;
          detailLevel: "detailed" | "standard" | "concise";
          reportTemplateId?: string | null;
          callSign?: string | null;
          investigationId?: string | null;
        }
      | {
          speakers: { name: string; role: string }[];
          includeTimestamps: boolean;
          additionalContext: string;
          detailLevel: "detailed" | "standard" | "concise";
          callSign?: string | null;
        },
    methodOrLegacy?:
      | string
      | {
          speakers: { name: string; role: string }[];
          includeTimestamps: boolean;
          additionalContext: string;
          detailLevel: "detailed" | "standard" | "concise";
          method?: string;
        },
    templateId?: string | null
  ) => {
    // Extract reportTemplateId from either direct param (AudioPlayback) or options (UploadModal)
    let reportTemplateId: string | undefined;
    if (templateId) {
      reportTemplateId = templateId;
    } else if (
      optionsOrAudioType &&
      typeof optionsOrAudioType === "object" &&
      "reportTemplateId" in optionsOrAudioType &&
      optionsOrAudioType.reportTemplateId
    ) {
      reportTemplateId = optionsOrAudioType.reportTemplateId;
    }

    // Investigation-for-context link (UploadModal, entitled officers only)
    let investigationId: string | null | undefined;
    if (
      optionsOrAudioType &&
      typeof optionsOrAudioType === "object" &&
      "investigationId" in optionsOrAudioType &&
      optionsOrAudioType.investigationId
    ) {
      investigationId = optionsOrAudioType.investigationId;
    }

    // Extract per-report call sign override from options (UploadModal or AudioPlayback)
    let callSign: string | null | undefined;
    if (
      optionsOrAudioType &&
      typeof optionsOrAudioType === "object" &&
      "callSign" in optionsOrAudioType &&
      optionsOrAudioType.callSign
    ) {
      callSign = optionsOrAudioType.callSign;
    }

    await processAudioFile(
      filesOrEvent,
      optionsOrAudioType,
      methodOrLegacy,
      undefined,
      {
        user,
        onSuccess: (caseData) => {
          if (caseData.case) {
            // Data from polling success
            onNewCase(
              caseData.case,
              caseData.score,
              caseData.suggestions,
              caseData.transcription
            );
          } else {
            // Direct case data
            onNewCase(caseData, undefined, [], null);
          }
        },
        onError: (error) => {
          console.error("Processing failed:", error);
        },
        onCancelled: (discardedCaseId) => {
          // Notify parent that a case was discarded so it can reload and select next case
          onCaseDiscarded(discardedCaseId);
        },
        createNewCase: true,
        reportTemplateId,
        callSign,
        investigationId,
      }
    );
  };

  const loadAvatar = () => {
    if (user?.avatarUrl) {
      return <img src={user.avatarUrl} alt="" />;
    } else if (user?.name) {
      return (
        <div className="text-white font-semibold text-lg absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          {`${user.name.split(" ")[0][0]}${
            user.name.split(" ")[user.name.split(" ").length - 1]?.[0] || ""
          }`}
        </div>
      );
    }
  };

  const handleBackButton = () => {
    setIsMobileCase(false);
    setIsMobileMenuOpen(false);
  };

  const handleArrowClick = () => {
    const newValue = !isMenuCollapsed;
    setIsMenuCollapsed(newValue);
    localStorage.setItem("pnai-isMenuCollapsed", JSON.stringify(newValue));
  };

  // Add function to fetch checklist templates
  const fetchChecklistTemplates = useCallback(async () => {
    try {
      const response = await axios.get("/api/checklist-templates");
      setChecklistTemplates(response.data.templates || []);
    } catch (error) {
      console.error("Error fetching checklist templates:", error);
      setChecklistTemplates([]);
    }
  }, []);

  // Fetch templates and evidence integration when component mounts
  useEffect(() => {
    if (user?.agency_id) {
      fetchChecklistTemplates();
      axios
        .get("/api/settings/evidence-integration")
        .then((res) => setHasEvidenceIntegration(!!res.data.integration))
        .catch(() => setHasEvidenceIntegration(false));
      policiesApi
        .available()
        .then(setHasPolicyDocuments)
        .catch(() => setHasPolicyDocuments(false));
    } else {
      setHasPolicyDocuments(false);
    }
  }, [user?.agency_id, fetchChecklistTemplates]);

  return (
    <>
      {/* Mobile Menu Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-base-200 border-b-2 border-base-300 z-50 flex items-center px-4 justify-between">
        <button
          onClick={() =>
            isMobileCase
              ? handleBackButton()
              : setIsMobileMenuOpen(!isMobileMenuOpen)
          }
          className="p-2"
        >
          {isMobileCase ? (
            <ChevronLeftIcon className="h-6 w-6" />
          ) : isMobileMenuOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </button>
        <div className="mb-4 block md:hidden">
          <img
            src={badgeSrc}
            className="w-8 mt-5"
            alt="Police Narratives AI Badge"
          />
        </div>
      </div>

      {/* Main Menu Container */}
      <div
        style={{ "--panel-w": `${navPanel.width}px` } as CSSProperties}
        className={cn(
          isMenuCollapsed ? "w-20 md:w-20" : "md:w-[var(--panel-w)]",
          "bg-base-200 border-r-2 border-base-300 flex flex-col h-screen",
          !navPanel.isResizing && "transition-all duration-300",
          "fixed md:relative top-0 left-0 z-40",
          "md:translate-x-0",
          isMobileCase
            ? "-translate-x-full"
            : isMobileMenuOpen
              ? "translate-x-0"
              : "-translate-x-full",
          "pt-16 md:pt-0", // Add padding top on mobile for header
        )}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {!isMenuCollapsed && (
          <ResizeHandle edge="right" active={navPanel.isResizing} className="!z-10" {...navPanel.handleProps} />
        )}
        {/* Arrow Left Icon that shows on hover */}
        <div
          className={cn(
            // z-20 keeps the button above the panel's resize handle (z-10), which
            // otherwise catches the click along the sidebar's right edge.
            "absolute top-1/2 transform -translate-y-1/2 right-[-22px] z-20 p-2 rounded-full bg-blue-500 hover:bg-blue-600 cursor-pointer transition-all duration-200",
            isHovering ? "opacity-100" : "opacity-0",
          )}
          onClick={handleArrowClick}
        >
          <div className="flex justify-center items-center">
            {!isMenuCollapsed ? (
              <ChevronLeftIcon className="size-6 text-white relative right-[1px]" />
            ) : (
              <ChevronRightIcon className="size-6 text-white relative left-[1px]" />
            )}
          </div>
        </div>

        <AudioPlayback
          modalRef={modalRef}
          handleAudioFileChange={handleAudioFileChange}
          showReportSettings
          onSaveToFiles={(blob, name, type) =>
            saveFilesToLibrary(
              [
                {
                  file: new File([blob], `recording-${Date.now()}.wav`, { type: "audio/wav" }),
                  displayName: name || defaultRecordingName(),
                  mediaType: "audio",
                  audioType: type === "interaction" ? "interaction" : "narrative",
                  libraryOther: type === "other",
                  speakers: [],
                  languages: [],
                  additionalContext: "",
                },
              ],
              "recording",
            )
          }
        />
        <UploadModal
          modalRef={uploadModalRef}
          onUpload={handleAudioFileChange}
          hasEvidenceIntegration={hasEvidenceIntegration}
          allowDocuments
          initialFiles={uploadPrefill}
        />
        {/* Fills the space between the top and the pinned footer. The logo and
            New Case button stay put (the New Case menu is wider than the
            collapsed rail and must not be clipped); only the nav icons and
            categories below them scroll, so a tall list never pushes the footer
            (theme toggle, settings) below the window or makes the page scroll. */}
        <div className={cn("flex-1 min-h-0 flex flex-col", !isMenuCollapsed ? "p-6" : "p-3")}>
          <div className="mb-4 flex justify-center">
            {isMenuCollapsed ? (
              <img
                src={badgeSrc}
                className="w-12 h-auto"
                alt="Police Narratives AI Badge"
              />
            ) : (
              <Logo className="w-full h-auto" />
            )}
          </div>
          <div className="grid gap-y-2">
            <div
              className={cn(
                "grid grid-cols-10",
                !isMenuCollapsed
                  ? "gap-x-2"
                  : "gap-y-1 border-b pb-2 border-base-300 mb-2",
              )}
            >
              <div
                className={cn(!isMenuCollapsed ? "col-span-7" : "col-span-10")}
                data-tour="new-case-dropdown"
              >
                <NewCaseDropdown
                  onRecordAudio={handleOnRecordAudioClick}
                  onUploadAudio={handleOnUploadAudioClick}
                  onBlankReport={handleNewCase}
                  showChecklist={checklistTemplates.length > 0}
                  onChecklist={() => setShowChecklistModal(true)}
                  buttonText="New Case"
                  buttonVariant="primary"
                  className="w-full"
                  isMenuCollapsed={isMenuCollapsed}
                  disabled={isDesignerOpen}
                />
              </div>
              <div
                className={cn(!isMenuCollapsed ? "col-span-3" : "col-span-10")}
              >
                <Button
                  className="btn btn-active border-0 w-full"
                  tabIndex={0}
                  variant="secondary"
                  data-tour="preferences-button"
                  onClick={() => {
                    navigate("/preferences");
                    setIsMobileMenuOpen(false);
                  }}
                >
                  <AdjustmentsHorizontalIcon
                    className={cn(!isMenuCollapsed ? "size-5" : "size-6")}
                  />
                </Button>
              </div>
            </div>
          </div>
          <input
            type="file"
            ref={audioInputRef}
            onChange={handleAudioFileChange}
            accept="audio/*,.m4a,.mp3,.wav"
            className="hidden"
          />
          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden scrollbar-hide -mx-1 px-1">
          <div
            className={cn(
              `grid`,
              !isMenuCollapsed ? "mt-6 gap-y-1" : "grid-cols-10 gap-y-1",
            )}
          >
            <div
              className={cn(isMenuCollapsed && "col-span-10")}
              data-tour="cases-nav"
            >
              <MenuItem
                page="cases"
                active={
                  location.pathname.startsWith("/cases") &&
                  activeFilter.type === "all"
                }
                onNavigate={() => {
                  setActiveFilter({ type: "all", categoryId: null });
                  setIsMobileMenuOpen(false);
                }}
                isMenuCollapsed={isMenuCollapsed}
              >
                {!isMenuCollapsed && <p>Cases</p>}
              </MenuItem>
            </div>
            <div className={cn(isMenuCollapsed && "col-span-10")} data-tour="trash-nav">
              <MenuItem
                page="trash"
                onNavigate={() => setIsMobileMenuOpen(false)}
                isMenuCollapsed={isMenuCollapsed}
                onDragOver={allowCaseDrop}
                onDragLeave={() => setIsTrashDragOver(false)}
                onDrop={handleTrashDrop}
                className={cn(
                  "transition-colors",
                  // Text color hints droppable while dragging; transitions to a
                  // background fill when hovered.
                  isDraggingCase && !isTrashDragOver && "text-red-400",
                  isTrashDragOver && "bg-red-100"
                )}
              >
                {!isMenuCollapsed && <p>Trash</p>}
              </MenuItem>
            </div>
            <div className={cn(isMenuCollapsed && "col-span-10")} data-tour="files-nav">
              <MenuItem
                page="files"
                onNavigate={() => setIsMobileMenuOpen(false)}
                isMenuCollapsed={isMenuCollapsed}
              >
                {!isMenuCollapsed && <p>Files</p>}
              </MenuItem>
            </div>
            <div className={cn(isMenuCollapsed && "col-span-10")} data-tour="forms-nav">
              <MenuItem
                page="forms"
                onNavigate={() => setIsMobileMenuOpen(false)}
                isMenuCollapsed={isMenuCollapsed}
              >
                {!isMenuCollapsed && <p>Forms</p>}
              </MenuItem>
            </div>
            <div className={cn(isMenuCollapsed && "col-span-10")} data-tour="redactions-nav">
              <MenuItem
                page="redactions"
                onNavigate={() => setIsMobileMenuOpen(false)}
                isMenuCollapsed={isMenuCollapsed}
              >
                {!isMenuCollapsed && <p>Redactions</p>}
              </MenuItem>
            </div>
            {/* Investigations is a per-seat add-on; the item only renders
                for entitled officers. Direct navigation hits an upsell page. */}
            {addons.investigations?.entitled && (
              <div className={cn(isMenuCollapsed && "col-span-10")}>
                <MenuItem
                  page="investigations"
                  onNavigate={() => setIsMobileMenuOpen(false)}
                  isMenuCollapsed={isMenuCollapsed}
                >
                  {!isMenuCollapsed && <p>Investigations</p>}
                </MenuItem>
              </div>
            )}
            {/* Policies: the department's own policy manual and ordinances,
                browsable and askable. Only once the agency has uploaded at
                least one document; a department without any never sees it. */}
            {hasPolicyDocuments && (
              <div className={cn(isMenuCollapsed && "col-span-10")} data-tour="policies-nav">
                <MenuItem
                  page="policies"
                  onNavigate={() => setIsMobileMenuOpen(false)}
                  isMenuCollapsed={isMenuCollapsed}
                >
                  {!isMenuCollapsed && <p>Policies</p>}
                </MenuItem>
              </div>
            )}
            {/* Feature Requests is for paying customers: any agency member or an
                active personal subscription. Trial users don't see it. */}
            {canUseFeatureRequests && (
            <div className={cn(isMenuCollapsed && "col-span-10")}>
              <MenuItem
                page="feature-requests"
                onNavigate={() => setIsMobileMenuOpen(false)}
                isMenuCollapsed={isMenuCollapsed}
              >
                {!isMenuCollapsed && <p>Feature Requests</p>}
              </MenuItem>
            </div>
            )}
            {/* Academy: lessons on every feature, for every signed-in officer
                (trial, lapsed, solo, agency). The pill counts lessons that
                shipped after they joined and have not been opened yet. */}
            {SHOW_ACADEMY_NAV && (
            <div
              className={cn(isMenuCollapsed && "col-span-10")}
              data-tour="academy-nav"
            >
              <MenuItem
                page="academy"
                onNavigate={() => setIsMobileMenuOpen(false)}
                isMenuCollapsed={isMenuCollapsed}
                badge={academyBadge}
              >
                {!isMenuCollapsed && <p>Academy</p>}
              </MenuItem>
            </div>
            )}
          </div>
          <CaseCategories
            isMenuCollapsed={isMenuCollapsed}
            onNavigate={() => setIsMobileMenuOpen(false)}
          />
          </div>
        </div>
        {/* User Badge */}
        <div
          data-tour="settings-button"
          className={cn(
            "border-t px-4 mt-auto hover:cursor-pointer group",
            !isMenuCollapsed ? "py-2" : "py-4",
          )}
          onClick={() => {
            navigate("/settings");
            setIsMobileMenuOpen(false);
          }}
        >
          {isMenuCollapsed ? (
            <div className="flex flex-col items-center gap-3">
              <button
                aria-label="Toggle dark mode"
                className="p-1 rounded hover:bg-base-300"
                onClick={(e) => {
                  e.stopPropagation();
                  setTheme(resolvedTheme === "dark" ? "light" : "dark");
                }}
              >
                {resolvedTheme === "dark" ? (
                  <SunIcon className="size-5 text-base-content/50 hover:text-base-content/80" />
                ) : (
                  <MoonIcon className="size-5 text-base-content/50 hover:text-base-content/80" />
                )}
              </button>
              <Cog8ToothIcon
                className={cn(
                  "size-6 text-base-content/60 opacity-50 group-hover:opacity-80 transition-opacity",
                  location.pathname.startsWith("/settings") &&
                    "!text-blue-500 !opacity-100 transition-opacity",
                )}
              />
            </div>
          ) : (
            /* Flex rather than a 12-column grid: the fixed 3/6/3 split gave the
               icons a share of the width instead of their intrinsic size, so
               the settings gear fell outside the sidebar once the nav dropped
               to its 12rem width. Below xl the nav can't fit avatar + name +
               icons on one line, so the name/badge block is dropped there and
               the avatar sits alongside the icons; both return at xl. */
            <div className="flex items-center gap-x-2 xl:gap-x-3">
              <div
                className="avatar flex shrink-0 cursor-pointer"
                title={user?.name || "Name here"}
              >
                <div className="rounded-full overflow-hidden relative bg-blue-500 flex items-center justify-center aspect-square w-10 min-w-[2.5rem] min-h-[2.5rem]">
                  {loadAvatar()}
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                className="hidden"
              />
              {/* min-w-0 + truncate guard against unusually long names, which
                  would otherwise push the icons out of the sidebar. */}
              <div className="text hidden min-w-0 flex-1 items-center xl:flex">
                <div className="flex flex-col justify-between h-full w-full min-w-0 py-2">
                  <p
                    className="font-bold text-sm h-auto truncate leading-tight"
                    title={user?.name || "Name here"}
                  >
                    {user?.name || "Name here"}
                  </p>
                  <p
                    className="text-base-content/60 text-sm truncate"
                    title={
                      user?.badgeID ? `Badge #${user?.badgeID}` : `Set Badge #`
                    }
                  >
                    {user?.badgeID ? `Badge #${user?.badgeID}` : `Set Badge #`}
                  </p>
                </div>
              </div>
              {/* ml-auto pins the icons right once the name block is hidden. */}
              <div className="ml-auto flex shrink-0 items-center justify-end gap-1">
                <button
                  aria-label="Toggle dark mode"
                  className="p-1 rounded hover:bg-base-300"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTheme(resolvedTheme === "dark" ? "light" : "dark");
                  }}
                >
                  {resolvedTheme === "dark" ? (
                    <SunIcon className="size-5 text-base-content/50 hover:text-base-content/80" />
                  ) : (
                    <MoonIcon className="size-5 text-base-content/50 hover:text-base-content/80" />
                  )}
                </button>
                <Cog8ToothIcon
                  className={cn(
                    "size-6 text-base-content/60 opacity-50 group-hover:opacity-80 transition-opacity",
                    location.pathname.startsWith("/settings") &&
                      "!text-blue-500 !opacity-100 transition-opacity",
                  )}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Menu Overlay - Always mounted */}
      <div
        className={cn(
          "md:hidden fixed inset-0 bg-black-500/35 backdrop-blur-sm z-30",
          "transition-all duration-300 ease-in-out",
          isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible",
        )}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Checklist Modal */}
      {showChecklistModal && (
        <ChecklistModal
          templates={checklistTemplates}
          onClose={() => setShowChecklistModal(false)}
          onComplete={(newCase) => {
            setShowChecklistModal(false);
            onChecklistComplete(newCase);
          }}
        />
      )}
    </>
  );
});

export default Menu;
